<template>
  <woot-modal
    :show.sync="show"
    :on-close="onClose"
    size="modal-big"
    content-class="email-templates-conversation-modal"
  >
    <woot-modal-header
      :header-title="$t('EMAIL_TEMPLATES.MODAL.TITLE')"
      :header-content="modalHeaderContent"
    />
    <div class="row modal-content">
      <templates-picker
        v-if="!selectedTemplate"
        @onSelect="pickTemplate"
      />
      <div v-else class="w-full template-preview parser-layout">
        <!-- Live preview (same pattern as WhatsApp TemplateParser) -->
        <div class="preview-block">
          <div class="preview-section">
            <span class="preview-kicker">{{
              $t('EMAIL_TEMPLATES.MODAL.SUBJECT_LABEL')
            }}</span>
            <p class="preview-subject-value">{{ resolvedSubject }}</p>
          </div>
          <div class="preview-section preview-section--body">
            <span class="preview-kicker">{{
              $t('EMAIL_TEMPLATES.MODAL.BODY_LABEL')
            }}</span>
            <textarea
              :value="resolvedBodyPlain"
              readonly
              rows="5"
              class="template-input"
              :aria-label="$t('EMAIL_TEMPLATES.MODAL.BODY_LABEL')"
            />
          </div>
        </div>

        <div
          v-if="numericVariableIndices.length"
          class="template__variables-container"
        >
          <p class="variables-label">
            {{ $t('WHATSAPP_TEMPLATES.PARSER.VARIABLES_LABEL') }}
          </p>
          <div
            v-for="idx in numericVariableIndices"
            :key="idx"
            class="template__variable-item"
          >
            <span class="variable-label">{{ variableToken(idx) }}</span>
            <woot-input
              v-model="variableValues[String(idx)]"
              type="text"
              class="variable-input"
              :styles="{ marginBottom: 0 }"
              :placeholder="
                $t('EMAIL_TEMPLATES.MODAL.VARIABLE_PLACEHOLDER', { n: idx })
              "
            />
          </div>
          <p
            v-if="$v.variableValues.$error && $v.$dirty"
            class="error"
          >
            {{ $t('WHATSAPP_TEMPLATES.PARSER.FORM_ERROR_MESSAGE') }}
          </p>
        </div>

        <div
          v-if="selectedTemplate.attachments && selectedTemplate.attachments.length"
          class="template__variables-container attachments-section"
        >
          <p class="variables-label">
            {{ $t('EMAIL_TEMPLATES.MODAL.ATTACHMENTS_LABEL') }}
          </p>
          <ul class="attachment-list attachment-list--scroll">
            <li
              v-for="att in selectedTemplate.attachments"
              :key="att.id || att.filename"
              class="attachment-list-item"
            >
              <fluent-icon icon="attach" size="14" class="mr-1 flex-shrink-0" />
              <span>{{ att.filename }}</span>
            </li>
          </ul>
        </div>

        <footer>
          <woot-button
            variant="smooth"
            :disabled="isSending"
            @click="onResetTemplate"
          >
            {{ $t('WHATSAPP_TEMPLATES.PARSER.GO_BACK_LABEL') }}
          </woot-button>
          <woot-button
            :is-loading="isSending"
            :disabled="isSending"
            @click="onSendTemplate"
          >
            {{ $t('WHATSAPP_TEMPLATES.PARSER.SEND_MESSAGE_LABEL') }}
          </woot-button>
        </footer>
      </div>
    </div>
  </woot-modal>
</template>

<script>
import TemplatesPicker from './TemplatesPicker.vue';

const NUMERIC_VAR_RE = /\{\{\s*(\d+)\s*\}\}/g;

function collectNumericVariableIndices(subject, body) {
  const set = new Set();
  const scan = text => {
    if (!text) return;
    const re = new RegExp(NUMERIC_VAR_RE.source, 'g');
    let m;
    while ((m = re.exec(text)) !== null) {
      set.add(Number(m[1]));
    }
  };
  scan(subject || '');
  scan(body || '');
  return Array.from(set).sort((a, b) => a - b);
}

function plainTextFromHtml(html) {
  if (!html) return '';
  return String(html)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const allVariableSlotsFilled = value => {
  if (!value || typeof value !== 'object') return true;
  const keys = Object.keys(value);
  if (!keys.length) return true;
  return keys.every(k => String(value[k] ?? '').trim() !== '');
};

export default {
  components: {
    TemplatesPicker,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  validations: {
    variableValues: {
      allVariableSlotsFilled,
    },
  },
  data() {
    return {
      selectedTemplate: null,
      variableValues: {},
      isSending: false,
    };
  },
  computed: {
    modalHeaderContent() {
      return this.selectedTemplate
        ? this.$t('EMAIL_TEMPLATES.MODAL.TEMPLATE_SELECTED_SUBTITLE', {
            templateName: this.selectedTemplate.name,
          })
        : this.$t('EMAIL_TEMPLATES.MODAL.SUBTITLE');
    },
    numericVariableIndices() {
      if (!this.selectedTemplate) return [];
      return collectNumericVariableIndices(
        this.selectedTemplate.subject,
        this.selectedTemplate.body
      );
    },
    resolvedSubject() {
      if (!this.selectedTemplate) return '';
      return this.replaceNumericVars(this.selectedTemplate.subject || '');
    },
    resolvedBody() {
      if (!this.selectedTemplate) return '';
      return this.replaceNumericVars(this.selectedTemplate.body || '');
    },
    resolvedBodyPlain() {
      return plainTextFromHtml(this.resolvedBody);
    },
  },
  watch: {
    show(newVal) {
      if (!newVal) {
        this.selectedTemplate = null;
        this.variableValues = {};
        this.isSending = false;
        this.$nextTick(() => this.$v && this.$v.$reset());
      }
    },
  },
  methods: {
    variableToken(idx) {
      return `{{${idx}}}`;
    },
    pickTemplate(template) {
      this.selectedTemplate = template;
      this.initVariableValues(template);
      this.$nextTick(() => this.$v && this.$v.$reset());
    },
    initVariableValues(template) {
      const indices = collectNumericVariableIndices(
        template.subject,
        template.body
      );
      const next = {};
      indices.forEach(i => {
        next[String(i)] = '';
      });
      this.variableValues = next;
    },
    replaceNumericVars(text) {
      if (!text) return '';
      return text.replace(/\{\{\s*(\d+)\s*\}\}/g, (match, idx) => {
        const v = this.variableValues[String(idx)];
        return v != null && v !== '' ? v : match;
      });
    },
    onResetTemplate() {
      this.selectedTemplate = null;
      this.variableValues = {};
      this.$nextTick(() => this.$v && this.$v.$reset());
    },
    onSendTemplate() {
      if (!this.selectedTemplate) return;
      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.isSending = true;
      const attachmentSignedIds = (this.selectedTemplate.attachments || [])
        .map(a => a.signed_id)
        .filter(Boolean);
      this.$emit('on-send', {
        subject: this.resolvedSubject,
        body: this.resolvedBody,
        attachmentSignedIds,
      });
      this.selectedTemplate = null;
      this.variableValues = {};
      this.$nextTick(() => {
        this.isSending = false;
        if (this.$v) this.$v.$reset();
      });
    },
    onClose() {
      this.selectedTemplate = null;
      this.variableValues = {};
      this.isSending = false;
      this.$nextTick(() => this.$v && this.$v.$reset());
      this.$emit('cancel');
    },
  },
};
</script>

<style scoped lang="scss">
.modal-content {
  padding: 1.5625rem 2rem;
}

.parser-layout {
  @apply w-full;
}

.preview-block {
  @apply mb-2.5;
}

.preview-section {
  @apply mb-1;
}

.preview-section--body {
  @apply mt-5 pt-4 border-t border-slate-100 dark:border-slate-700;
}

.preview-kicker {
  @apply text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide block mb-1.5;
}

.preview-subject-value {
  @apply text-sm text-slate-900 dark:text-slate-100 m-0 leading-relaxed;
}

.preview-section--body .template-input {
  @apply mt-0;
}

/* Match WhatsappTemplates/TemplateParser.vue */
.template__variables-container {
  @apply p-2.5;
}

.variables-label {
  @apply text-sm font-semibold mb-2.5;
}

.template__variable-item {
  @apply items-center flex mb-2.5;

  .variable-input {
    @apply flex-1 text-sm ml-2.5 min-w-0;
  }

  .variable-label {
    @apply bg-slate-75 dark:bg-slate-700 text-slate-700 dark:text-slate-100 inline-block rounded-md text-xs py-2.5 px-6 flex-shrink-0;
  }
}

.attachments-section {
  @apply pt-0;
}

.attachment-list {
  @apply list-none m-0 p-0 text-sm text-slate-700 dark:text-slate-300;
}

.attachment-list--scroll {
  @apply max-h-48 overflow-y-auto pr-1;
}

.attachment-list-item {
  @apply flex items-center py-1;
}

footer {
  @apply flex justify-end mt-4;

  button {
    @apply ml-2.5;
  }
}

.error {
  @apply bg-red-100 dark:bg-red-900/30 rounded-md text-red-800 dark:text-red-200 p-2.5 text-center text-sm mt-2;
}

.template-input {
  @apply w-full rounded-md border border-slate-100 dark:border-slate-700 bg-slate-25 dark:bg-slate-900 text-slate-700 dark:text-slate-100 text-sm p-2.5 resize-y min-h-[5rem];
}
</style>
